import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { getPredictionMarkets } from '../utils/api';

const Markets = () => {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);
  const [filteredMarkets, setFilteredMarkets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('volume');
  const [viewMode, setViewMode] = useState('grid');

  const categories = [
    { id: 'all', label: 'All Markets', icon: 'Grid3X3' },
    { id: 'Politics', label: 'Politics', icon: 'Vote' },
    { id: 'Cryptocurrency', label: 'Crypto', icon: 'Bitcoin' },
    { id: 'Stocks', label: 'Stocks', icon: 'TrendingUp' },
    { id: 'Technology', label: 'Tech', icon: 'Cpu' },
    { id: 'Sports', label: 'Sports', icon: 'Trophy' },
    { id: 'Climate', label: 'Climate', icon: 'Globe' },
    { id: 'Space', label: 'Space', icon: 'Rocket' }
  ];

  const sortOptions = [
    { id: 'volume', label: 'Volume', icon: 'BarChart3' },
    { id: 'newest', label: 'Newest', icon: 'Clock' },
    { id: 'ending-soon', label: 'Ending Soon', icon: 'Timer' },
    { id: 'alphabetical', label: 'A-Z', icon: 'ArrowUpDown' }
  ];

  useEffect(() => {
    const fetchMarkets = async () => {
      setIsLoading(true);
      try {
        const response = await getPredictionMarkets('all', 50);
        if (response?.success) {
          setMarkets(response?.data || []);
          setFilteredMarkets(response?.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch markets:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkets();
  }, []);

  // Filter and sort markets
  useEffect(() => {
    let filtered = [...markets];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(market => 
        market?.category?.toLowerCase() === selectedCategory?.toLowerCase()
      );
    }

    // Filter by search query
    if (searchQuery?.trim()) {
      filtered = filtered?.filter(market =>
        market?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        market?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        market?.category?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Sort markets
    switch (sortBy) {
      case 'volume':
        filtered?.sort((a, b) => (b?.totalVolume || 0) - (a?.totalVolume || 0));
        break;
      case 'newest':
        filtered?.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        break;
      case 'ending-soon':
        filtered?.sort((a, b) => new Date(a.endDate || 0) - new Date(b.endDate || 0));
        break;
      case 'alphabetical':
        filtered?.sort((a, b) => (a?.title || '')?.localeCompare(b?.title || ''));
        break;
      default:
        break;
    }

    setFilteredMarkets(filtered);
  }, [markets, selectedCategory, searchQuery, sortBy]);

  const handleViewMarket = (marketId) => {
    navigate(`/market/${marketId}`);
  };

  const handleTradeMarket = (marketId) => {
    navigate(`/trading-dashboard?market=${marketId}`);
  };

  const formatVolume = (volume) => {
    if (volume >= 1000000) return `$${(volume / 1000000)?.toFixed(1)}M`;
    if (volume >= 1000) return `$${(volume / 1000)?.toFixed(0)}K`;
    return `$${volume}`;
  };

  const getOutcomeColor = (price) => {
    if (price > 0.7) return 'text-success';
    if (price > 0.5) return 'text-warning-400';
    return 'text-error';
  };

  const getDaysUntilEnd = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Ended';
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const getStatusColor = (endDate) => {
    const daysLeft = getDaysUntilEnd(endDate);
    if (daysLeft === 'Ended') return 'text-error';
    if (daysLeft === 'Today' || daysLeft === '1 day') return 'text-warning-400';
    return 'text-success';
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      <div className="px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-heading-bold text-text-primary mb-4">
            Prediction Markets
          </h1>
          <p className="text-lg text-text-secondary max-w-2xl">
            Trade on the outcomes of real-world events. From politics to crypto, sports to technology.
          </p>
        </div>

        {/* Filters & Search */}
        <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6 mb-8 backdrop-blur-sm">
          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
              <input
                type="text"
                placeholder="Search markets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="w-full pl-10 pr-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
              />
            </div>
          </div>

          {/* Category Filters */}
          <div className="mb-6">
            <h3 className="text-sm font-body-semibold text-text-primary mb-3">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {categories?.map((category) => (
                <button
                  key={category?.id}
                  onClick={() => setSelectedCategory(category?.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                    selectedCategory === category?.id
                      ? 'bg-primary-900 text-primary-300 border border-primary-800 glow-primary' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover border border-border hover:border-border-hover'
                  }`}
                >
                  <Icon name={category?.icon} size={16} />
                  <span>{category?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort & View Options */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-body-medium text-text-secondary">Sort by:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="bg-surface-hover border border-border rounded-trading px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
                >
                  {sortOptions?.map((option) => (
                    <option key={option?.id} value={option?.id}>
                      {option?.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-sm font-body-medium text-text-secondary">View:</span>
              <div className="flex items-center space-x-1 bg-surface-hover rounded-trading p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-trading transition-all duration-trading-fast ${
                    viewMode === 'grid' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name="Grid3X3" size={16} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-trading transition-all duration-trading-fast ${
                    viewMode === 'list' ? 'bg-primary text-white' : 'text-text-secondary hover:text-text-primary'
                  }`}
                >
                  <Icon name="List" size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <div className="text-sm text-text-secondary">
            {isLoading ? 'Loading...' : `${filteredMarkets?.length} markets found`}
          </div>
          
          {filteredMarkets?.length > 0 && (
            <button
              onClick={() => navigate('/trading-dashboard')}
              className="bg-gradient-primary text-white px-4 py-2 rounded-trading font-body-medium text-sm hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
            >
              Go to Trading Dashboard
            </button>
          )}
        </div>

        {/* Markets Grid/List */}
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(9)]?.map((_, index) => (
              <div key={index} className="bg-surface border border-border rounded-trading p-6 animate-pulse">
                <div className="h-4 bg-border rounded mb-4"></div>
                <div className="h-3 bg-border rounded mb-2"></div>
                <div className="h-3 bg-border rounded mb-4"></div>
                <div className="flex space-x-4">
                  <div className="h-8 bg-border rounded flex-1"></div>
                  <div className="h-8 bg-border rounded flex-1"></div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredMarkets?.length === 0 ? (
          <div className="text-center py-16">
            <Icon name="Search" size={48} className="text-text-secondary mx-auto mb-4" />
            <h3 className="text-xl font-body-semibold text-text-primary mb-2">
              No markets found
            </h3>
            <p className="text-text-secondary mb-6">
              Try adjusting your search or filter criteria
            </p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
              }}
              className="bg-gradient-primary text-white px-6 py-2 rounded-trading font-body-medium hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' :'space-y-4'
          }>
            {filteredMarkets?.map((market) => (
              <div
                key={market?.id}
                className={`bg-surface border border-border rounded-trading shadow-trading-card hover:shadow-trading-modal transition-all duration-trading-normal backdrop-blur-sm group ${
                  viewMode === 'list' ? 'p-4' : 'p-6'
                }`}
              >
                <div className={viewMode === 'list' ? 'flex items-center space-x-6' : ''}>
                  <div className={viewMode === 'list' ? 'flex-1' : ''}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                          <Icon name="TrendingUp" size={16} color="white" />
                        </div>
                        <span className="text-xs bg-primary-900 text-primary-300 px-2 py-1 rounded">
                          {market?.category}
                        </span>
                      </div>
                      <span className={`text-xs flex items-center ${getStatusColor(market?.endDate)}`}>
                        <Icon name="Clock" size={12} className="mr-1" />
                        {getDaysUntilEnd(market?.endDate)}
                      </span>
                    </div>

                    <h3 className="font-body-semibold text-text-primary mb-2 line-clamp-2 group-hover:text-primary transition-colors duration-trading-fast cursor-pointer"
                        onClick={() => handleViewMarket(market?.id)}>
                      {market?.title}
                    </h3>
                    
                    {viewMode === 'grid' && (
                      <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                        {market?.description}
                      </p>
                    )}
                  </div>

                  <div className={viewMode === 'list' ? 'flex items-center space-x-6' : ''}>
                    <div className={`grid grid-cols-2 gap-3 mb-4 ${viewMode === 'list' ? 'min-w-[200px]' : ''}`}>
                      {market?.outcomeTokens?.map((token, index) => (
                        <div key={index} className="bg-surface-hover rounded-trading p-3 text-center">
                          <p className="text-xs text-text-secondary mb-1">{token?.name}</p>
                          <p className={`font-data text-lg ${getOutcomeColor(token?.price)}`}>
                            ${token?.price?.toFixed(2)}
                          </p>
                        </div>
                      ))}
                    </div>

                    <div className={`flex items-center ${viewMode === 'list' ? 'flex-col space-y-2 min-w-[120px]' : 'justify-between'}`}>
                      <div className="text-sm text-text-secondary">
                        Volume: {formatVolume(market?.totalVolume)}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewMarket(market?.id)}
                          className="text-primary text-sm font-body-medium hover:text-primary-300 transition-colors duration-trading-fast flex items-center"
                        >
                          View
                          <Icon name="Eye" size={14} className="ml-1" />
                        </button>
                        <button
                          onClick={() => handleTradeMarket(market?.id)}
                          className="bg-gradient-primary text-white px-3 py-1 rounded text-sm font-body-medium hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
                        >
                          Trade
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Markets;